export type ServicesConfigId = 'find_tenant'
| 'unit_photography'
| 'finding_tenant_subscription_service'
| 'finding_tenant_one_time_service'
| 'request_inspection'
| 'service_pre_move_out_maintenance_inspection'
| 'property_rent_valuation'
| 'noc_generation'
| 'tenant_screening'
| 'contract_drafting'
| 'inventory_checklist'
| 'lease_execution'
| 'utility_setup'
| 'service_property_management'
| 'service_property_management_serene'
| 'service_property_management_harmony'
| 'service_property_management_signature'
| "service_unit_maintenance"
| 'service_gifting_property'
| 'service_rental_dispute'
| 'service_consultation_request'
| 'service_tmc'
| 'service_tmc_zenith'
| 'service_tmc_essence'
| 'service_tenant_renewal_retention'
| 'service_mortgage_or_property_liquidation'
| 'service_loan_against_rent'
| 'service_property_insurance'
| 'service_finding_buyer'
| 'service_property_value_report'
| 'service_property_sale_valuation'
| 'service_finding_property_rent'
| 'service_finding_property_sale'
| 'contract_renewal_drafting'
| 'sale_contract_progression'
| 'service_power_of_attorney'
| 'service_will_registration_assistance'
| 'service_legal_notice'
| 'service_preventive_maintenance_check'
| 'service_routine_property_inspection'
| 'service_snagging_property'
| 'service_bso_unit_viewing_request'
| 'service_bso_unit_offer_request'

export type ServiceAudienceRole = "landlord" | "tenant" | "developer"

export interface ServiceOrderBinding {
    unitRequired: boolean
    allowOrderWithoutUnit: boolean
}

export interface RelatedServicesConfig {
    included_free?: Array<{
        id: ServicesConfigId
        bundleEligible?: boolean
        backgroundSrc?: string
        priceOverride?: number
        priceTextOverride?: string
        discountPriceOverride?: number
        discountPriceTextOverride?: string
    }>
    optional_free?: Array<{
        id: ServicesConfigId
        bundleEligible?: boolean
        backgroundSrc?: string
        priceOverride?: number
        priceTextOverride?: string
        discountPriceOverride?: number
        discountPriceTextOverride?: string
    }>
    optional_paid?: Array<{
        id: ServicesConfigId
        bundleEligible?: boolean
        backgroundSrc?: string
        priceOverride?: number
        priceTextOverride?: string
        discountPriceOverride?: number
        discountPriceTextOverride?: string
    }>
    recommended?: ServicesConfigId[]
}

export interface ServicePlan {
    id: string
    orderID:number
    title?: string
    durationDays?: number
    description?: string
    name?: string
    price?: number
    priceText?: string
    discountPrice?: number
    discountPriceText?: string
    badgeText?: string
}

export type ServiceType = 'subscription' | 'on_demand' | 'microservice'
export type ServiceTypePlacement = ServiceType | ServiceType[]
export type ServiceCategory =
| 'PROPERTY MANAGEMENT'
| 'MAINTENANCE'
| 'PROMOTIONAL'
| 'CONSULTATION'
| 'LEGAL'
| 'FINANCE'
| 'SALES'

export type ServiceCatalogSection =
| 'MANAGEMENT & ASSISTANCE'
| 'MAINTENANCE'
| 'LEGAL'
| 'FINANCE'
| 'GENERAL'

export interface ServiceConfigItem {
    id: ServicesConfigId
    title: string
    description: string
    whyThisHelps?: string
    showStaticInfoInUnitCard?: boolean
    showWhyThisHelpsInUnitCard?: boolean
    category: ServiceCategory
    // UI-only grouping for services catalog sections.
    catalogSection?: ServiceCatalogSection
    serviceType: ServiceTypePlacement
    availability: {
        standalone: boolean
        related: boolean
    }
    groups?: string[]
    audienceRoles?: ServiceAudienceRole[]
    orderBinding?: ServiceOrderBinding
    relatedServices?: RelatedServicesConfig
    recommendedWith?: ServicesConfigId[]
    plans: ServicePlan[]
    iconSrc: string
    implemented: boolean
    popular?: boolean
}

export interface ServicesV2RegistryItem {
    id: string
    iconSrc: string
    description: string
    title: string
    category: ServiceCategory
    catalogSection?: ServiceCatalogSection
    serviceType: ServiceTypePlacement
    serviceTypes: ServiceType[]
    availability: ServiceConfigItem["availability"]
    audienceRoles?: ServiceAudienceRole[]
    orderBinding?: ServiceOrderBinding
    relatedServices?: RelatedServicesConfig
    recommendedWith?: ServicesConfigId[]
    implemented: boolean
    popular?: boolean
}
const TBD_PRICE = 0
const TBD_PRICE_TEXT = 'TBD'

export const normalizeServiceTypes = (value: ServiceTypePlacement): ServiceType[] => {
    const list = Array.isArray(value) ? value : [value]
    return Array.from(new Set(list))
}

export const serviceBelongsToType = (value: ServiceTypePlacement, target: ServiceType): boolean => {
    return normalizeServiceTypes(value).includes(target)
}

export const ServicesConfig = {
    getService (id: string): ServiceConfigItem | undefined {
        return this.config.find(service => service.id === id)
    },
    getPlan (serviceId: ServicesConfigId, id?: string): ServicePlan | undefined {
        const service = this.getService(serviceId)
        return service?.plans?.find(plan => id != null ? plan.id === id : plan.id === 'default')
    },
    config: [
        {
            id: 'find_tenant', 
            title: 'Find me a Tenant - Listing Service',
            description: "Activate 3,000+ agents to find a qualified tenant at the best market price.",
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'on_demand',
            groups: ['Find me a Tenant'],
            availability: { standalone: true, related: false },
            audienceRoles: ["landlord", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: false },
            relatedServices: {
                optional_paid: [
                    { id: "finding_tenant_subscription_service" },
                    { id: "finding_tenant_one_time_service" },
                    { id: "unit_photography", bundleEligible: true },
                    { id: "property_rent_valuation", bundleEligible: true },
                    { id: "inventory_checklist", bundleEligible: true },
                    { id: "lease_execution", bundleEligible: true },
                    { id: "request_inspection", bundleEligible: true },
                ],
                included_free: [
                    { id: "tenant_screening", bundleEligible: true },
                    { id: "contract_drafting", bundleEligible: true },
                    { id: "utility_setup", bundleEligible: true },
                    { id: "noc_generation", bundleEligible: false },
                ],
            },
            implemented: true,
            popular: true,
            plans: [
                // {
                //     id: '60_days',
                //     orderID:10,
                //     title: '60 days',
                //     durationDays: 60,
                //     name: 'Sixty Days Plan',
                //     description: 'Best for maximum market exposure',
                //     price: 169,
                //     priceText: '169',
                //     // discountPrice: 0 ,// 99,
                //     // discountPriceText: "0" , // '99',
                // },

                // {
                //     id: '45_days',
                //     orderID:12,
                //     title: '45 days',
                //     durationDays: 45,
                //     name: 'Forty Fife Days Plan',
                //     description: 'Best for fast-track results',
                //     price: 109,
                //     priceText: '109',
                //     // discountPrice: 0 ,// 99,
                //     // discountPriceText: "0" , // '99',
                // },
                {
                    id: '30_days',
                    orderID:12,
                    title: '30 days',
                    durationDays: 30,
                    name: 'Thirty Days Plan',
                    description: 'Ideal for immediate visibility',
                    price: 250,
                    priceText: '250',
                    discountPrice: 0 , // 99,
                    discountPriceText: "0" , // '99',
                },
               
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-tenant.svg',
        },
        {
            id: 'finding_tenant_subscription_service',
            title: 'Find me a Tenant Subscription',
            description: 'We will find a replacement tenant whenever your property becomes vacant.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: true },
            groups: ['Find me a Tenant'],
            audienceRoles: ["landlord", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: false },
            relatedServices: {
                included_free: [
                    { id: "unit_photography" },
                    { id: "property_rent_valuation" },
                    { id: "tenant_screening" },
                    { id: "contract_drafting" },
                    { id: "inventory_checklist" },
                    { id: "lease_execution" },
                    { id: "utility_setup" },
                    { id: "request_inspection" },
                    { id: "noc_generation" },
                ],
                recommended: ["find_tenant"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 39,
                    name: 'Finding a Tenant Subscription Service',
                    description: '4% charged after finding a tenant.',
                    price: TBD_PRICE,
                    priceText: '4% after finding a tenant',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-tenant.svg',
        },
        {
            id: 'finding_tenant_one_time_service',
            title: 'One-time Finding a Tenant Order',
            description: 'Single-use assistance to find a qualified tenant at market price.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'on_demand',
            availability: { standalone: false, related: true },
            groups: ['Find me a Tenant'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: false },
            relatedServices: {
                included_free: [
                    { id: "unit_photography" },
                    { id: "property_rent_valuation" },
                    { id: "tenant_screening" },
                    { id: "contract_drafting" },
                    { id: "inventory_checklist" },
                    { id: "lease_execution" },
                    { id: "utility_setup" },
                    { id: "request_inspection" },
                    { id: "noc_generation" },
                ],
                recommended: ["find_tenant"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 52,
                    name: 'Finding a Tenant One Time Service',
                    description: '4% charged after finding a tenant.',
                    price: TBD_PRICE,
                    priceText: '4% after finding a tenant',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-tenant.svg',
        },
        {
            id: 'unit_photography', 
            title: 'Pro Photography & Listing Prep', 
            description: 'Get your property marketing ready to stand out on every top portal.',
            category: 'PROMOTIONAL',
            serviceType: 'microservice',
            availability: { standalone: true, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID:13,
                    name: 'Pro Photography & Listing Prep',
                    title: undefined,
                    durationDays: undefined,
                    description: 'Get your property marketing ready to stand out on every top portal.',
                    discountPrice: 299,
                    discountPriceText: '299',
                    price: 500,
                    priceText: '500'
                }
            ],
            iconSrc: '/assets/media/landlord/services/icons/camera.svg'
        },
        {
            id: 'request_inspection',  
            title: 'Pre-Move-In Maintenance Inspection',
            description: "Detailed photo report of your property’s condition to prevent future damage disputes.",
            category: 'MAINTENANCE',
            serviceType: 'microservice',
            availability: { standalone: true, related: true },
            groups: ['Maintenance Services'],
            audienceRoles: ["landlord", "tenant", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "Total Protection: You get a comprehensive photo report that serves as undeniable evidence of our property's condition, preventing maintenance disputes and protecting you from unfair damage claims when the tenant moves out. ",

            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 22,
                    title: undefined,
                    name: 'Pre-Move-In Maintenance Inspection',
                    durationDays: undefined,
                    description: "Detailed photo report of your property's condition to prevent future damage disputes.",
                    price: 199,
                    priceText: '199'
                }
            ],
            iconSrc: '/assets/media/landlord/services/icons/inspection.svg'
        },

        {
            id: 'property_rent_valuation', // RELATED TO find_tenant CAN CHOOSE
            title: 'Property Value Report for Rent',
            description: "Get an expert report to price your property perfectly for today’s market.",
            category: 'PROMOTIONAL',
            serviceType: 'microservice',
            availability: { standalone: true, related: true },
            groups: ['Property Value Report'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID:14,
                    title: undefined,
                    name: 'Property Value Report for Rent',
                    durationDays: undefined,
                    description: "Get an expert report to price your property perfectly for today's market.",
                    price: 299,
                    priceText: '299',
                    // discountPrice: 150,
                    // discountPriceText: '150',
                }
            ],
            iconSrc: '/assets/media/landlord/services/icons/business-graph.svg'
        },
        {
            id: 'noc_generation', 
            title: 'Broker Marketing Permit Distribution & Management',
            description: "We arrange NOCs and legal docs to publish your property on top portals.",
            category: 'LEGAL',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: " Proper authorization removes compliance blockers, allowing your listing to go live faster and reach more qualified leads. ",

            implemented: true,
            plans: [

                {
                    id: 'default',
                    orderID: 21,
                    name: 'Broker Marketing Permit Distribution & Management',
                    title: undefined,
                    durationDays: undefined,
                    description: "Promotion of the property through BSO's broker network of 3,000+ active agents to accelerate tenant sourcing and increase qualified inquiries.",
                    price: 0,
                    priceText: 'FREE'
                }
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg'
        },
   
        {
            id: 'tenant_screening',
            title: 'Tenant Screening Assistance',
            description: "Verify your tenant’s background and documents before you sign the contract.",
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "Screening lowers your risk by ensuring the tenant is financially stable and protects your unit from avoidable legal or maintenance issues.",

            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 15,
                    name: 'Tenant Screening Assistance',
                    description: "Verify your tenant's background and documents before you sign the contract.",
                    price: 0,
                    priceText: 'FREE',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/tenant_screening.png',
        },
        {
            id: 'contract_drafting',// RELATED TO find_tenant COVERED
            title: 'Digital Progression of Rental Contract',
            description: 'Secure your deal instantly with RERA-compliant digital lease agreements.',
            category: 'LEGAL',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "A legally binding, digitally signed contract removes ambiguity, speeds up the closing process, and keeps all parties aligned on clear terms.",
            showStaticInfoInUnitCard: true,
            showWhyThisHelpsInUnitCard: true,
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 16,
                    name: 'Digital Progression of Rental Contract',
                    description: 'Secure your deal instantly with RERA-compliant digital lease agreements.',
                    price: 0,
                    priceText: 'FREE',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'inventory_checklist', // RELATED TO find_tenant COVERED
            title: 'Inventory Documentation Assistance',
            description: 'Protect your furniture and appliances with a detailed, documented list.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'microservice',
            availability: { standalone: true, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "Having a documented baseline protects your assets and reduces handover disputes when the tenant moves out",
            showStaticInfoInUnitCard: true,
            showWhyThisHelpsInUnitCard: true,
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 17,
                    name: 'Inventory Documentation Assistance',
                    description: 'Protect your furniture and appliances with a detailed, documented list.',
                    price: 99,
                    priceText: '99',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/inspection.svg',
        },
        {
            id: 'lease_execution', // RELATED TO find_tenant COVERED
            title: 'Lease Execution & Move-In Coordination',
            description: 'Let us handle keys and onboarding of your new tenant.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'microservice',
            availability: { standalone: true, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "Structured move-in coordination keeps key handover, tenant onboarding, and start-of-tenancy logistics on track.",
            showStaticInfoInUnitCard: true,
            showWhyThisHelpsInUnitCard: true,
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 18,
                    name: 'Lease Execution & Move-in Coordination',
                    description: 'Let us handle keys and onboarding of your new tenant.',
                    price: 99,
                    priceText: '99',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/lease-execition.svg' // '/assets/media/landlord/services/icons/inspection-check.svg',
        },
        {
            id: 'utility_setup',
            title: 'Utility Activation Supervision',
            description: 'We make sure your tenant has activated all required utilities before move-in day.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["find_tenant"],
            },
            whyThisHelps: "Confirmed utility activation before move-in helps prevent handover delays, avoids billing disputes, and keeps the tenant onboarding process smooth.",
            showStaticInfoInUnitCard: true,
            showWhyThisHelpsInUnitCard: true,
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 19,
                    name: 'Utility Activation Supervision',
                    description: 'We make sure your tenant has activated all required utilities before move-in day.',
                    price: 105,
                    priceText: '105 / account',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/inspection-check.svg',
        },



        
        {
            id: 'service_property_management', 
            title: 'Property Management Subscription',
            description: 'Manage your property from anywhere: we handle tenants and maintenance while you track it all online.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: true, related: false },
            groups: ['Subscriptions'],
            audienceRoles: ["landlord", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: [
                    "tenant_screening",
                    "property_rent_valuation",
                    "request_inspection",
                    "service_unit_maintenance",
                    "unit_photography",
                ],
            },
            implemented: true,
            popular: true,
            plans: [
                {
                    id: 'default',
                    orderID: 26,
                    name: 'Online Property Management',
                    description: 'Manage your property from anywhere: we handle tenants and maintenance while you track it all online.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/property_management.png',
        },
        {
            id: 'service_property_management_serene',
            title: 'Property Management Subscription — Serene',
            description: 'Serene property management subscription.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: false },
            audienceRoles: ['landlord', 'developer'],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [{ id: 'serene', orderID: 26, name: 'Serene', price: 0, priceText: '0' }],
            iconSrc: '/assets/media/landlord/services/mainPngs/property_management.png',
        },
        {
            id: 'service_property_management_harmony',
            title: 'Property Management Subscription — Harmony',
            description: 'Harmony property management subscription.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: false },
            audienceRoles: ['landlord', 'developer'],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [{ id: 'harmony', orderID: 39, name: 'Harmony', price: 0, priceText: '0' }],
            iconSrc: '/assets/media/landlord/services/mainPngs/property_management.png',
        },
        {
            id: 'service_property_management_signature',
            title: 'Property Management Subscription — Signature',
            description: 'Signature property management subscription.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: false },
            audienceRoles: ['landlord', 'developer'],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [{ id: 'signature', orderID: 40, name: 'Signature', price: 0, priceText: '0' }],
            iconSrc: '/assets/media/landlord/services/mainPngs/property_management.png',
        },
        {
            id: 'service_unit_maintenance', 
            title: 'One-time Maintenance Order',
            description: 'Expert repair management available on a risk-free, one-time basis.',
            category: 'MAINTENANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Maintenance Services'],
            audienceRoles: ["landlord", "tenant", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: [
                    "service_preventive_maintenance_check",
                    "request_inspection",
                    "service_pre_move_out_maintenance_inspection",
                    "service_snagging_property",
                ],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 24,
                    name: 'One time Maintenance Order',
                    description:'Submit a one-time maintenance order for immediate repair coordination.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/fixing.svg',
        },

        {
            id: 'service_gifting_property',  
            title: 'Property Gifting Supervision',
            description: 'Expert guidance to legally transfer your property to family members.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Main Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 33,
                    name: 'Property Gifting Supervision',
                    description: 'Expert guidance to legally transfer your property to family members.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/gifting_property.png',
        },
      
        {
            id: 'service_rental_dispute',  
            title: 'Rental Dispute Assistance', 

            // add only form   What type of service do you need?
            description: 'Expert guidance to resolve tenant conflicts and navigate legal processes without the stress.',
            category: 'LEGAL',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Main Services'],
            audienceRoles: ["landlord", "tenant"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 25,
                    name: 'Rental Dispute Assistance',
                    description: 'Expert guidance to resolve tenant conflicts and navigate legal processes without the stress.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/rental_dispute.png',
        },
       
    
        {
            id: 'service_tenant_renewal_retention',  
            title: 'Lease Renewal & Ejari Supervision',
            description: 'We negotiate with your tenant for the best market rent and handle the full Ejari process.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Main Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                optional_paid: [
                    { id: "contract_renewal_drafting" },
                ],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 23,
                    name: 'Lease Renewal & Ejari Supervision',
                    description: 'We negotiate with your tenant for the best market rent and handle the full Ejari process.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/tenant_screening.png',
        },
        {
            id: 'service_mortgage_or_property_liquidation',  
            title: 'Mortgage & Sales Supervision',
            description: 'Expert guidance to help you clear your mortgage or sell your property for the best price.',
            category: 'FINANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Financial & Insurance Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 27,
                    name: 'Mortgage & Sales Supervision',
                    description: 'Expert guidance to help you clear your mortgage or sell your property for the best price',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/buying_property.png',
        },
        {
            id: 'service_loan_against_rent',  
            title: 'Loan Against Rent Assistance',
            description: "Did you know your rent can be a loan? Get your full year’s cash upfront and reinvest now.",
            category: 'FINANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Financial & Insurance Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 28,
                    name: 'Loan Against Rent Assistance',
                    description: "Did you know your rent can be a loan? Get your full year's cash upfront and reinvest now.",
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/account_balance.svg',
        },
        {
            id: 'service_property_insurance',  
            title: 'Property Insurance Assistance',
            description: "Protect your investment from fire, floods, and tenant damage with the right insurance coverage.",
            category: 'FINANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Financial & Insurance Services'],
            audienceRoles: ["landlord", "tenant"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 29,
                    name: 'Property Insurance Assistance',
                    description: 'We help you find the right insurance for your property, covering fire, damage, and unexpected incidents. It\'s a small cost that protects your investment and gives you complete peace of mind.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/shield_with_heart.svg',
        },
        {
            id: 'service_finding_buyer',  
            title: 'Find me a Buyer',
            description: 'Activate 3,000+ agents to find a qualified buyer at the best market price.',
            category: 'SALES',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Main Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                optional_paid: [
                    { id: "sale_contract_progression" },
                ],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 38,
                    name: 'Find me a Buyer',
                    description: 'Get end-to-end support to market your unit, attract qualified buyers, and close with confidence.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc:  '/assets/media/landlord/services/icons/finding-buyer.svg',
        },
        {
            id: 'service_finding_property_rent',
            title: 'Find me a Property For Rent',
            description: 'Sourcing assistance to find and secure your next rental home.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Find me a Property'],
            audienceRoles: ["landlord", "tenant"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 43,
                    name: 'Find me a Property For Rent',
                    description: 'Get support finding a rental property that matches your requirements and timeline.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-buyer.svg',
        },
        {
            id: 'service_finding_property_sale',
            title: 'Find me a Property For Sale',
            description: 'Sourcing assistance to find and buy your next property investment.',
            category: 'SALES',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Find me a Property'],
            audienceRoles: ["landlord", "tenant"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            relatedServices: {
                optional_paid: [
                    { id: "sale_contract_progression" },
                ],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 44,
                    name: 'Find me a Property For Sale',
                    description: 'Get support finding a property to buy with guided search and acquisition coordination.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-buyer.svg',
        },
        {
            id: 'service_property_value_report',
            title: 'Property Value Report for Rent/Sale',
            description: "Get an expert report to price your property perfectly for today’s market.",
            category: 'FINANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Property Value Report'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 50,
                    name: 'Property Value Report for Rent/Sale',
                    description: "Get an expert report to price your property perfectly for today's market.",
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/business-graph.svg',
        },
        {
            id: 'service_property_sale_valuation',  
            title: 'Property Value Report for Sale',
            description: "Get an expert report to price your property perfectly for today’s market.",
            category: 'SALES',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Property Value Report'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 30,
                    name: 'Property Value Report for Sale',
                    description: "Get an expert report to price your property perfectly for today's market.",
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/finding-buyer.svg',
        },
        {
            id: 'contract_renewal_drafting',
            title: 'Digital Progression of Rental Contract Renewal',
            description: 'Supervision of tenancy contract signing and the Ejari process.',
            category: 'LEGAL',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["service_tenant_renewal_retention"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 51,
                    name: 'Digital Progression of Rental Contract Renewal',
                    description: 'Coordinate rental contract renewal documentation and digital progression for an existing tenancy.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'sale_contract_progression',
            title: 'Digital Progression of Sale Contract',
            description: 'Expert supervision to finalize sale contracts and clear titles.',
            category: 'LEGAL',
            serviceType: 'microservice',
            availability: { standalone: false, related: true },
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            relatedServices: {
                recommended: ["service_finding_buyer"],
            },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 48,
                    name: 'Digital Progression of Sale Contract',
                    description: 'Coordinate sale contract documentation and digital progression for a property transaction.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'service_power_of_attorney',
            title: 'Power of Attorney',
            description: 'Professional assistance for drafting and signing a Power of Attorney.',
            category: 'LEGAL',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Notary Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 55,
                    name: 'Power of Attorney',
                    description: 'Request assistance preparing and coordinating a power of attorney process.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'service_will_registration_assistance',
            title: 'Will Writing & Registration Assistance',
            description: 'Expert supervision to legally register and protect your legacy.',
            category: 'LEGAL',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Notary Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 56,
                    name: 'Will Writing & Registration Assistance',
                    description: 'Request assistance with will writing and registration coordination.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'service_legal_notice',
            title: 'Legal Notice',
            description: 'Assistance in drafting and serving formal RERA-compliant notices.',
            category: 'LEGAL',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Notary Services', 'Main Services'],
            audienceRoles: ["landlord"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 57,
                    name: 'Legal Notice',
                    description: 'Request assistance preparing and serving a legal notice.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/purchase-order.svg',
        },
        {
            id: 'service_preventive_maintenance_check',
            title: 'Preventative Maintenance Subscription',
            description: 'Stop problems before they start with proactive checks that prevent expensive emergency repairs.',
            category: 'MAINTENANCE',
            serviceType: 'subscription',
            availability: { standalone: true, related: false },
            groups: ['Subscriptions', 'Maintenance Services'],
            audienceRoles: ["landlord", "tenant", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 31,
                    name: 'Preventative Maintenance Subscription',
                    description: 'Recurring preventative maintenance coverage to reduce emergency repairs and protect asset condition.',
                    price: 0,
                    priceText: '0',
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/snagging.svg',
        },
        {
            id: 'service_pre_move_out_maintenance_inspection',
            title: 'Pre-Move-Out Maintenance Inspection',
            description: 'Professional oversight to identify repairs before a tenant vacates.',
            category: 'MAINTENANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Maintenance Services'],
            audienceRoles: ["landlord", "tenant", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 41,
                    name: 'Pre-Move-Out Maintenance Inspection',
                    description: 'Assess unit condition before move-out and flag maintenance items early for a smoother handover.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/inspection.svg',
        },
        {
            id: 'service_snagging_property',
            title: 'Brand New Property Snagging',
            description: 'Detailed inspection before accepting handover from your developer.',
            category: 'MAINTENANCE',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            groups: ['Maintenance Services'],
            audienceRoles: ["landlord", "developer"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 42,
                    name: 'Brand New Property Snagging',
                    description: 'Comprehensive snagging support for newly handed-over properties before occupancy.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/snagging.svg',
        },
        {
            id: 'service_consultation_request', 
            title: 'Consultation',
            description: 'Submit your details for expert guidance on your rental situation.',
            category: 'CONSULTATION',
            serviceType: 'on_demand',
            availability: { standalone: true, related: false },
            audienceRoles: ["landlord", "tenant", "developer"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 32, // 40
                    name: 'Request a Consultation',
                    description: 'Not sure which service you need? Send a consultation request and our team will contact you with the best next steps.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            // icon fallback (replace when design provides)
            iconSrc: '/assets/media/landlord/services/icons/inspection.svg',
        },
        {
            id: 'service_tmc',
            title: 'Tenancy Management Subscription',
            description: 'Full-service tenant support for maintenance, renewals, and legal.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: true, related: false },
            groups: ['Subscriptions'],
            audienceRoles: ["tenant"],
            orderBinding: { unitRequired: true, allowOrderWithoutUnit: false },
            implemented: true,
            plans: [
                {
                    id: 'default',
                    orderID: 37,
                    name: 'Tenant Management Contract (TMC)',
                    description: 'Structured tenant management support for tenant-created units, with guided onboarding and ongoing contract operations.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/mainPngs/tenant_screening.png',
        },
        {
            id: 'service_tmc_zenith',
            title: 'Tenancy Management Contract — Zenith',
            description: 'Zenith tenancy management contract.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: false },
            audienceRoles: ['tenant'],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [{ id: 'zenith', orderID: 37, name: 'Zenith', price: 0, priceText: '0' }],
            iconSrc: '/assets/media/landlord/services/mainPngs/tenant_screening.png',
        },
        {
            id: 'service_tmc_essence',
            title: 'Tenancy Management Contract — Essence',
            description: 'Essence tenancy management contract.',
            category: 'PROPERTY MANAGEMENT',
            serviceType: 'subscription',
            availability: { standalone: false, related: false },
            audienceRoles: ['tenant'],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: true,
            plans: [{ id: 'essence', orderID: 46, name: 'Essence', price: 0, priceText: '0' }],
            iconSrc: '/assets/media/landlord/services/mainPngs/tenant_screening.png',
        },
        {
            id: 'service_bso_unit_viewing_request',
            title: 'Viewing Request',
            description: 'Coordination assistance for property viewings of BSO Club units.',
            category: 'CONSULTATION',
            serviceType: 'on_demand',
            availability: { standalone: false, related: false },
            groups: ['Main Services'],
            audienceRoles: ["tenant"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: false,
            plans: [
                {
                    id: 'default',
                    orderID: 34,
                    name: 'BSO Club Unit: Viewing Request',
                    description: 'Submit a viewing request for this BSO Club catalogue unit.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/viewing.svg',
        },
        {
            id: 'service_bso_unit_offer_request',
            title: 'Offer Request',
            description: 'Professional assistance in submitting offers for BSO Club units.',
            category: 'SALES',
            serviceType: 'on_demand',
            availability: { standalone: false, related: false },
            groups: ['Main Services'],
            audienceRoles: ["tenant"],
            orderBinding: { unitRequired: false, allowOrderWithoutUnit: true },
            implemented: false,
            plans: [
                {
                    id: 'default',
                    orderID: 35,
                    name: 'BSO Club Unit: Offer Request',
                    description: 'Submit an offer request for this BSO Club catalogue unit.',
                    price: TBD_PRICE,
                    priceText: TBD_PRICE_TEXT,
                },
            ],
            iconSrc: '/assets/media/landlord/services/icons/offer.svg',
        },
    ] as ServiceConfigItem[]
}

// These concrete subscriptions intentionally override their catalog/flow parent plan IDs.
// Keep this separate from UI card configuration: it is the canonical order ProductID map.
export const CURRENT_SUBSCRIPTION_PRODUCT_SERVICE_IDS: Record<number, ServicesConfigId> = {
    26: 'service_property_management_serene',
    37: 'service_tmc_zenith',
    39: 'service_property_management_harmony',
    40: 'service_property_management_signature',
    46: 'service_tmc_essence',
}

// UI/route compatibility card ids. Canonical service identity remains ServicesConfig.id.
const UI_CARD_ID_BY_CANONICAL_SERVICE_ID: Partial<Record<
ServicesConfigId,
string
>> = {
    find_tenant: 'service_finding_tenant',
    unit_photography: 'service_unit_photography',
    request_inspection: 'service_request_inspection',
    property_rent_valuation: 'service_unit_pvr',
    noc_generation: 'service_noc_generation',
    tenant_screening: 'service_tenant_screening',
    contract_drafting: 'service_contract_drafting_digital_signing',
    inventory_checklist: 'service_inventory_checklist_creation',
    lease_execution: 'service_lease_execution_move_in_coordination',
    utility_setup: 'service_utility_setup_verification',
    service_property_management: 'service_property_management',
    service_unit_maintenance: 'service_unit_maintenance',
    service_gifting_property: 'service_gifting_property',
    service_rental_dispute: 'service_rental_dispute',
    service_consultation_request: 'service_consultation_request',
    service_tmc: 'service_tmc',
    service_tenant_renewal_retention: 'service_tenant_renewal_retention',
    service_mortgage_or_property_liquidation: 'service_mortgage_or_property_liquidation',
    service_loan_against_rent: 'service_loan_against_rent',
    service_property_insurance: 'service_property_insurance',
    service_finding_buyer: 'service_finding_buyer',
    service_property_value_report: 'service_property_value_report',
    service_property_sale_valuation: 'service_property_sale_valuation',
    service_finding_property_rent: 'service_finding_property_rent',
    service_finding_property_sale: 'service_finding_property_sale',
    contract_renewal_drafting: 'service_contract_renewal_drafting',
    sale_contract_progression: 'service_sale_contract_progression',
    service_power_of_attorney: 'service_power_of_attorney',
    service_will_registration_assistance: 'service_will_registration_assistance',
    service_legal_notice: 'service_legal_notice',
    service_preventive_maintenance_check: 'service_preventive_maintenance_check',
    service_routine_property_inspection: 'service_routine_property_inspection',
    service_pre_move_out_maintenance_inspection: 'service_pre_move_out_maintenance_inspection',
    service_snagging_property: 'service_snagging_property',
    service_bso_unit_viewing_request: 'service_bso_unit_viewing_request',
    service_bso_unit_offer_request: 'service_bso_unit_offer_request',
}

const SERVICES_CATALOG_SECTION_BY_SERVICE_ID: Record<ServicesConfigId, ServiceCatalogSection> = {
    find_tenant: 'MANAGEMENT & ASSISTANCE',
    unit_photography: 'MANAGEMENT & ASSISTANCE',
    finding_tenant_subscription_service: 'MANAGEMENT & ASSISTANCE',
    finding_tenant_one_time_service: 'MANAGEMENT & ASSISTANCE',
    request_inspection: 'MAINTENANCE',
    service_pre_move_out_maintenance_inspection: 'MAINTENANCE',
    property_rent_valuation: 'FINANCE',
    noc_generation: 'LEGAL',
    tenant_screening: 'MANAGEMENT & ASSISTANCE',
    contract_drafting: 'LEGAL',
    inventory_checklist: 'MANAGEMENT & ASSISTANCE',
    lease_execution: 'MANAGEMENT & ASSISTANCE',
    utility_setup: 'MANAGEMENT & ASSISTANCE',
    service_property_management: 'MANAGEMENT & ASSISTANCE',
    service_property_management_serene: 'MANAGEMENT & ASSISTANCE',
    service_property_management_harmony: 'MANAGEMENT & ASSISTANCE',
    service_property_management_signature: 'MANAGEMENT & ASSISTANCE',
    service_unit_maintenance: 'MAINTENANCE',
    service_gifting_property: 'LEGAL',
    service_rental_dispute: 'LEGAL',
    service_consultation_request: 'MANAGEMENT & ASSISTANCE',
    service_tmc: 'MANAGEMENT & ASSISTANCE',
    service_tmc_zenith: 'MANAGEMENT & ASSISTANCE',
    service_tmc_essence: 'MANAGEMENT & ASSISTANCE',
    service_tenant_renewal_retention: 'LEGAL',
    service_mortgage_or_property_liquidation: 'FINANCE',
    service_loan_against_rent: 'FINANCE',
    service_property_insurance: 'FINANCE',
    service_finding_buyer: 'MANAGEMENT & ASSISTANCE',
    service_property_value_report: 'FINANCE',
    service_property_sale_valuation: 'FINANCE',
    service_finding_property_rent: 'MANAGEMENT & ASSISTANCE',
    service_finding_property_sale: 'MANAGEMENT & ASSISTANCE',
    contract_renewal_drafting: 'LEGAL',
    sale_contract_progression: 'LEGAL',
    service_power_of_attorney: 'LEGAL',
    service_will_registration_assistance: 'LEGAL',
    service_legal_notice: 'LEGAL',
    service_preventive_maintenance_check: 'MAINTENANCE',
    service_routine_property_inspection: 'MAINTENANCE',
    service_snagging_property: 'MAINTENANCE',
    service_bso_unit_viewing_request: 'GENERAL',
    service_bso_unit_offer_request: 'GENERAL',
}

for (const service of ServicesConfig.config) {
    service.catalogSection = SERVICES_CATALOG_SECTION_BY_SERVICE_ID[service.id]
}

const CORE_VISIBLE_CARD_IDS = new Set<string>([
    'service_finding_tenant',
    'service_tenant_renewal_retention',
    'service_rental_dispute',
    'service_tmc',
    'service_unit_maintenance',
    'service_property_management',
    'service_gifting_property',
    'service_mortgage_or_property_liquidation',
    'service_loan_against_rent',
    'service_property_insurance',
    'service_finding_buyer',
    'service_property_value_report',
    'service_property_sale_valuation',
    'service_finding_property_rent',
    'service_finding_property_sale',
    'service_power_of_attorney',
    'service_will_registration_assistance',
    'service_legal_notice',
    'service_preventive_maintenance_check',
    'service_routine_property_inspection',
    'service_snagging_property',
])





export const getServicesV2RegistryFromServicesConfig = ({
    coreVisibleOnly = true,
    standaloneOnly = false,
}: {
    coreVisibleOnly?: boolean
    standaloneOnly?: boolean
} = {}): ServicesV2RegistryItem[] => {
    return ServicesConfig.config.reduce<ServicesV2RegistryItem[]>((acc, service) => {
        const normalizedServiceTypes = normalizeServiceTypes(service.serviceType)
        const cardId = UI_CARD_ID_BY_CANONICAL_SERVICE_ID[service.id]
        if (cardId == null) return acc
        if (coreVisibleOnly && !CORE_VISIBLE_CARD_IDS.has(cardId)) return acc
        if (standaloneOnly && !service.availability.standalone) return acc
      
        acc.push({
            id: cardId,
            iconSrc: service.iconSrc,
            description: service.description,
            title: service.title,
            category: service.category,
            catalogSection: service.catalogSection,
            serviceType: normalizedServiceTypes[0] ?? "on_demand",
            serviceTypes: normalizedServiceTypes,
            availability: service.availability,
            audienceRoles: service.audienceRoles,
            orderBinding: service.orderBinding,
            relatedServices: service.relatedServices,
            recommendedWith: service.relatedServices?.recommended,
            implemented: service.implemented,
            popular: service.popular,
        })
        return acc
    }, [])
}

export const isServiceAvailableStandalone = (id: ServicesConfigId): boolean => {
    return ServicesConfig.getService(id)?.availability.standalone === true
}

export const isServiceAvailableRelated = (id: ServicesConfigId): boolean => {
    return ServicesConfig.getService(id)?.availability.related === true
}

export const getRecommendedServiceIdsFor = (id: ServicesConfigId): ServicesConfigId[] => {
    const service = ServicesConfig.getService(id)
    if (Array.isArray(service?.relatedServices?.recommended)) return service.relatedServices.recommended
    return Array.isArray(service?.recommendedWith) ? service.recommendedWith : []
}
